import './styles.css';

import React, { useEffect, useState } from 'react';
import {
    getAccountInternalAdmin,
    getAssociatedNetworks,
    getNetworkFeatures,
    isLoading,
    sendNotificationtoAccount,
    updateNetwork,
    handleAccountStatus,
    upsertLicense,
} from 'redux/slicers/accounts';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ResetPassword from './ResetPassword';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Dialog from '../../widgets/dialog';
import Switch from '../../widgets/switch';
import Tab from '@material-ui/core/Tab';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography/Typography';
import { selectPermissions } from 'redux/slicers/permissions';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import { Select } from '../../widgets/select';
import ImplementationGuide from './utils/implementation-guide';
import { UserConversionData } from './tabs/UserConversionData';
import { useSplit } from '../../../hooks/useSplit';
import { SPLIT_CONSTANTS } from '../../../services/split.io/constants';
import { InfoTab } from './info-tab/InfoTab';
import { LicensesService } from '../../../services/api/licenses.service';
import { AccountsService } from '../../../services/api/accounts.service';
import ajax from '../../../services/ajax';
import { ReportUploadManager } from './tabs/ReportUploadManager';

const useWarnStyles = makeStyles(() => ({
    warnTooltip: { backgroundColor: '#fff', padding: 0, maxWidth: 400, boxShadow: '0 3px 8px rgba(0,0,0,0.3)' },
}));

const AccountView = () => {
    const warnClasses = useWarnStyles();
    const params = useParams();
    const accountId = parseInt(params.accountId);
    const theme = useTheme();
    const history = useHistory();
    const dispatch = useDispatch();

    const { isOn: isUserConversionDataFlagOn } = useSplit(SPLIT_CONSTANTS.SPLIT_USER_CONVERSION_DATA);
    const { isOn: useLegacyMainTag } = useSplit(SPLIT_CONSTANTS.ADMIN_MAIN_TAG_LEGACY_FALLBACK);

    const [tab, setTab] = React.useState('info');
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [dialogImplemetationGuideIsOpen, setDialogImplemetationGuideIsOpen] = useState(false);
    const [dialogControlGroupIsOpen, setDialogControlGroupIsOpen] = useState(false);
    const [dialogResetPasswordIsOpen, setDialogResetPasswordIsOpen] = useState(false);
    const [tagInfo, setTagInfo] = useState(0);

    const permissions = useSelector(selectPermissions);
    const account = useSelector((state) => state.accounts.accounts.find((a) => a.id === accountId));

    const networks = account?.networks;
    const userCanWriteAccounts = account && permissions.account && permissions.account?.write;

    const [network] = networks || [];

    // Fetched on demand when the user asks for the implementation guide, not on page open: the
    // request gets-or-creates the main tag, so loading it eagerly made a side-effecting call (and
    // toasted any failure) just for viewing an account.
    const openImplementationGuide = async () => {
        if (tagInfo && tagInfo.networkId === network.id) {
            setDialogImplemetationGuideIsOpen(true);
            return;
        }
        try {
            const {
                whiteLabelDomain: { domain, cdn, cdnPath, server },
            } = network;
            const { id, tagHash, name } = useLegacyMainTag
                ? await ajax({ baseRoute: 'clickTrueTags/get_main_tag' })('', { networkId: network.id })
                : await AccountsService.getOrCreateMainTag(network.id);
            setTagInfo({
                networkId: network.id,
                tagHash,
                id,
                domain,
                cdn,
                cdnPath,
                server,
                name: encodeURIComponent(name),
                dataLocationId: account.dataLocationId,
                accountName: network.name,
            });
            setDialogImplemetationGuideIsOpen(true);
        } catch (e) {
            toast.error(e?.message || 'Failed to load the tag implementation details');
        }
    };

    useEffect(() => {
        if (!networks && !dispatch(isLoading('getAssociatedNetworks'))) dispatch(getAssociatedNetworks({ accountId }));
    }, [accountId, dispatch, networks]);

    useEffect(() => {
        if (network && !network.networkFeatures && account) {
            dispatch(getNetworkFeatures({ networkId: network.id, accountId: account.id }));
        }
        if (account && !account.internalAdmin) {
            dispatch(getAccountInternalAdmin({ accountName: account.name }));
        }
    }, [account, network, dispatch]);

    function createTabPanelId(name) {
        return `full-width-tabpanel-${name}`;
    }
    function createTabId(name) {
        return `full-width-tab-${name}`;
    }

    function a11yProps(name) {
        return {
            id: createTabId(name),
            name: name,
            'aria-controls': createTabPanelId(name),
        };
    }

    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <Typography component="div" role="tabpanel" hidden={value !== index} id={`full-width-tabpanel-${index}`} aria-labelledby={`full-width-tab-${index}`} {...other}>
                <Box p={3}>{children}</Box>
            </Typography>
        );
    }

    const onEdit = () => {
        history.push(`/accounts/edit/${account?.id}`);
    };

    const onSendInvite = () => {
        dispatch(sendNotificationtoAccount(account));
    };

    const renderEditButton = () => {
        if (userCanWriteAccounts) {
            return (
                <button className="cq-btn" onClick={onEdit}>
                    EDIT
                </button>
            );
        }
        return <></>;
    };

    async function onActivateDeActivateAccount({ orgId, accountId, networkId, active }) {
        if (!active) {
            await LicensesService.deleteAllProduction(networkId);
            dispatch(upsertLicense({ network_id: networkId, products: [], accountId: accountId }));
        }
        dispatch(handleAccountStatus({ orgId: orgId, accountId: accountId, networkId: networkId, active: active }));
    }

    const getTabsProps = () => {
        const tabs = { info: { Panel: <InfoTab accountId={accountId} account={account} networks={networks} />, label: 'Info' } };

        if (isUserConversionDataFlagOn && userCanWriteAccounts) {
            tabs['customerConversionData'] = { Panel: <UserConversionData network={network} />, label: 'Customer Conversion Data' };
        }

        if (userCanWriteAccounts && network) {
            tabs['uploadedReports'] = {
                Panel: <ReportUploadManager networkId={network.id} />,
                label: 'Uploaded Reports',
            };
        }

        return tabs;
    };

    const tabsProps = getTabsProps();

    const onActionChange = ({ value }) => {
        // eslint-disable-next-line default-case
        switch (value) {
            case 'activationEmail':
                onSendInvite();
                break;
            case 'implementationGuide':
                openImplementationGuide();
                break;
        }
    };

    return (
        <div>
            <div className="accounts-header">
                <div className="accounts-header-actions">
                    <div className="accounts-header-item">{renderEditButton()}</div>
                </div>
            </div>
            <div className="cq-container">
                <Card style={{ overflow: 'auto' }}>
                    <CardHeader
                        avatar={<Avatar src={account?.logoUrl} />}
                        title={account?.name}
                        subheader={
                            <span>
                                {account?.active ? '(Active)' : '(Deactivated)'}
                                {!!account?.active && Array.isArray(networks) && !networks.some((n) => n.active) && (
                                    <Tooltip
                                        classes={{ tooltip: warnClasses.warnTooltip }}
                                        title={
                                            <Alert variant="outlined" severity="error" style={{ fontSize: 14 }}>
                                                This account is marked active but none of its networks are active. An account should be active only while at least one network is active.
                                            </Alert>
                                        }
                                    >
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                backgroundColor: '#d32f2f',
                                                color: '#fff',
                                                fontSize: 13,
                                                fontWeight: 'bold',
                                                marginLeft: 6,
                                                cursor: 'help',
                                                verticalAlign: 'middle',
                                            }}
                                        >
                                            !
                                        </span>
                                    </Tooltip>
                                )}
                            </span>
                        }
                        action={
                            <div>
                                {/*C4PA-516 TO-DO: change SELECT to MENU */}
                                <Select
                                    value=""
                                    options={[
                                        { value: 'activationEmail', label: 'Send activation email' },
                                        network && { value: 'implementationGuide', label: 'Get implementation guide' },
                                    ]}
                                    placeholder={'Actions'}
                                    onChange={onActionChange}
                                />

                                <Switch
                                    type="checkbox"
                                    checked={!!account?.active}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.nativeEvent.stopImmediatePropagation();
                                        setDialogIsOpen(true);
                                    }}
                                />
                            </div>
                        }
                    />
                    <CardContent>
                        <Tabs
                            value={tab}
                            onChange={(e, newValue) => {
                                setTab(newValue);
                            }}
                        >
                            {Object.entries(tabsProps).map((currTab) => {
                                const [key, { label }] = currTab;
                                return <Tab key={key} value={key} label={label} {...a11yProps(label)} />;
                            })}
                        </Tabs>
                        <TabPanel key={tab} value={tab} index={tab} dir={theme.direction}>
                            {tabsProps[tab]?.Panel ?? tabsProps[Object.keys(tabsProps)[0]]}
                        </TabPanel>
                    </CardContent>
                </Card>
            </div>
            <Dialog
                onClose={() => setDialogIsOpen(false)}
                isOpen={dialogIsOpen}
                onSubmit={() => {
                    onActivateDeActivateAccount({ orgId: account?.orgId, accountId: account?.id, networkId: account?.networkId, active: !account?.active });
                }}
                title={account?.active ? 'Deactivate Account' : 'Activate Account'}
                body={account?.active ? `Are you sure you want to deactivate ${account?.name}?` : `Do you want to activate ${account?.name}?`}
            />
            <Dialog
                onClose={() => setDialogControlGroupIsOpen(false)}
                isOpen={dialogControlGroupIsOpen}
                onSubmit={() => {
                    const network = networks[0];
                    if (!network) return;
                    dispatch(
                        updateNetwork({
                            id: network.id,
                            isBlockingControlGroup: !network?.isBlockingControlGroup,
                            accountId: account?.id,
                        })
                    );
                }}
                title={networks && networks[0]?.isBlockingControlGroup ? 'Deactivate Blocking Control Group' : 'Activate Blocking Control Group'}
                body={networks && networks[0]?.isBlockingControlGroup ? `Are you sure you want to deactivate control group?` : `Do you want to activate control group?`}
            />
            <Dialog Component={() => <ImplementationGuide tagInfo={tagInfo} onclose={() => setDialogImplemetationGuideIsOpen(false)} />} isOpen={dialogImplemetationGuideIsOpen} />
            <Dialog isOpen={dialogResetPasswordIsOpen} Component={ResetPassword} props={{ account, setDialogResetPasswordIsOpen }} />
        </div>
    );
};

export default AccountView;
